import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../Components/Navigation';
import Footer from '../Components/Footer';

const Section = ({ title, children }) => (
    <div className="mb-10">
        <h2 className="text-xl font-bold text-midnight mb-4">{title}</h2>
        <div className="text-gray-600 leading-relaxed space-y-3">{children}</div>
    </div>
);

const TermsOfUse = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans antialiased text-gray-900 bg-white">
            <Navigation solid />

            {/* Header */}
            <div className="bg-midnight pt-36 pb-16">
                <div className="max-w-4xl mx-auto px-6 text-center">
                    <span className="inline-block bg-pawOrange/20 text-pawOrange text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
                        Condições de Uso
                    </span>
                    <h1 className="text-4xl font-extrabold text-white font-display mb-4">
                        Termos de Uso
                    </h1>
                    <p className="text-gray-400 text-base">
                        Última atualização: 20 de março de 2026
                    </p>
                </div>
            </div>

            {/* Content */}
            <main className="max-w-4xl mx-auto px-6 py-16">

                <Section title="1. Aceitação dos Termos">
                    <p>
                        Bem-vindo à <strong>PawSuite</strong>. Ao criar uma conta, acessar ou utilizar a plataforma
                        PawSuite — incluindo o sistema web, o aplicativo móvel e quaisquer serviços associados —
                        você ("Usuário" ou "Pet Shop") declara ter lido, compreendido e concordado com estes Termos
                        de Uso e com nossa{" "}
                        <Link to="/privacidade" className="text-tealVitality hover:underline font-medium">
                            Política de Privacidade
                        </Link>
                        .
                    </p>
                    <p>
                        Se você estiver aceitando estes Termos em nome de uma empresa ou pessoa jurídica, declara
                        ter poderes para vinculá-la a estas condições. Caso não concorde com qualquer disposição,
                        não utilize a plataforma.
                    </p>
                    <p>
                        <strong>PawSuite Technologies Ltda.</strong>, CNPJ nº 00.000.000/0001-00, doravante
                        denominada <strong>"PawSuite"</strong>, é a fornecedora dos serviços descritos neste documento.
                    </p>
                </Section>

                <Section title="2. Descrição dos Serviços">
                    <p>
                        A PawSuite é uma plataforma SaaS (Software como Serviço) desenvolvida para gestão de
                        pet shops, clínicas veterinárias e estabelecimentos afins. Os serviços incluem, sem limitação:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>Agenda inteligente de banho, tosa e consultas;</li>
                        <li>Prontuário digital de pets e histórico de serviços;</li>
                        <li>Dashboard financeiro e relatórios de desempenho;</li>
                        <li>Portal do tutor com notificações automáticas (app móvel);</li>
                        <li>Gestão de estoque, equipe e fluxo de caixa;</li>
                        <li>Suporte técnico dentro dos limites do plano contratado.</li>
                    </ul>
                    <p>
                        A PawSuite reserva-se o direito de adicionar, modificar ou descontinuar funcionalidades,
                        comunicando alterações relevantes com antecedência razoável.
                    </p>
                </Section>

                <Section title="3. Cadastro e Segurança da Conta">
                    <p>
                        Para utilizar a plataforma, é necessário criar uma conta com informações verdadeiras,
                        completas e atualizadas. O Usuário é responsável por:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>Manter a confidencialidade de suas credenciais de acesso (login e senha);</li>
                        <li>Todas as atividades realizadas com suas credenciais;</li>
                        <li>Notificar imediatamente a PawSuite em caso de acesso não autorizado ou suspeita de violação de segurança, pelo e-mail{" "}
                            <a href="mailto:suporte@pawsuite.com.br" className="text-tealVitality hover:underline">suporte@pawsuite.com.br</a>;</li>
                        <li>Garantir que colaboradores cadastrados como usuários secundários utilizem a plataforma em conformidade com estes Termos.</li>
                    </ul>
                    <p>
                        É vedado compartilhar credenciais entre usuários distintos. Cada acesso deve corresponder
                        a um indivíduo identificado. A PawSuite não se responsabiliza por perdas decorrentes de
                        uso não autorizado das credenciais do Usuário.
                    </p>
                </Section>

                <Section title="4. Planos, Preços e Pagamentos">
                    <p>
                        Os serviços são disponibilizados mediante assinatura mensal, conforme os planos descritos
                        na página de{" "}
                        <Link to="/#pricing" className="text-tealVitality hover:underline font-medium">
                            Planos e Preços
                        </Link>
                        . Condições gerais:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>
                            <strong>Cobrança:</strong> mensal, antecipada, com renovação automática no mesmo dia
                            do mês em que a assinatura foi realizada.
                        </li>
                        <li>
                            <strong>Reajuste:</strong> os preços podem ser reajustados anualmente com aviso prévio
                            de 30 dias, limitado à variação do IPCA do período.
                        </li>
                        <li>
                            <strong>Período de teste:</strong> quando disponível, o período gratuito não requer
                            dados de pagamento e encerra automaticamente ao fim do prazo.
                        </li>
                        <li>
                            <strong>Inadimplência:</strong> o não pagamento dentro do prazo resultará na suspensão
                            do acesso após 5 dias e no cancelamento após 15 dias, sem prejuízo da cobrança dos
                            valores devidos.
                        </li>
                        <li>
                            <strong>Reembolso:</strong> não há reembolso por frações de período já faturadas,
                            exceto nas hipóteses previstas no Código de Defesa do Consumidor.
                        </li>
                    </ul>
                    <p>
                        Os pagamentos são processados por meio de gateways certificados PCI-DSS. A PawSuite não
                        armazena dados completos de cartão de crédito.
                    </p>
                </Section>

                <Section title="5. Uso Permitido e Restrições">
                    <p>
                        O Usuário compromete-se a utilizar a plataforma exclusivamente para fins lícitos e em
                        conformidade com a legislação vigente. É expressamente proibido:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>Usar a plataforma para fins ilegais, fraudulentos ou que violem direitos de terceiros;</li>
                        <li>Tentar acessar sistemas, dados ou funcionalidades não autorizados (hacking, engenharia reversa);</li>
                        <li>Inserir vírus, malware ou qualquer código malicioso na plataforma;</li>
                        <li>Usar robôs, scrapers ou mecanismos automatizados para coleta de dados sem autorização;</li>
                        <li>Reproduzir, distribuir ou sublicenciar o software ou qualquer parte da plataforma;</li>
                        <li>Inserir dados falsos, imprecisos ou enganosos que possam prejudicar tutores, pets ou terceiros;</li>
                        <li>Utilizar a plataforma para envio de comunicações não solicitadas (spam).</li>
                    </ul>
                    <p>
                        A PawSuite reserva-se o direito de suspender ou encerrar a conta do Usuário que viole
                        estas disposições, sem aviso prévio e sem direito a reembolso.
                    </p>
                </Section>

                <Section title="6. Propriedade Intelectual">
                    <p>
                        Todo o conteúdo, tecnologia, código-fonte, marcas, logotipos, layouts, funcionalidades e
                        demais elementos da plataforma PawSuite são de titularidade exclusiva da PawSuite
                        Technologies Ltda. e protegidos pela legislação de propriedade intelectual brasileira
                        (Lei nº 9.279/1996 e Lei nº 9.610/1998).
                    </p>
                    <p>
                        A PawSuite concede ao Usuário uma licença limitada, não exclusiva, intransferível e
                        revogável para uso da plataforma, exclusivamente para os fins previstos nestes Termos e
                        pelo período da assinatura vigente.
                    </p>
                    <p>
                        Os dados inseridos pelo Usuário na plataforma (informações de clientes, pets, agendamentos)
                        permanecem de titularidade do Usuário. A PawSuite não reivindica propriedade sobre esse
                        conteúdo, utilizando-o apenas para a prestação dos serviços contratados.
                    </p>
                </Section>

                <Section title="7. Disponibilidade e Nível de Serviço (SLA)">
                    <p>
                        A PawSuite empenha-se para oferecer disponibilidade de <strong>99,5% ao mês</strong>
                        (excluindo janelas de manutenção programada). Manutenções preventivas serão comunicadas
                        com no mínimo 48 horas de antecedência por e-mail ou notificação na plataforma.
                    </p>
                    <p>
                        Não garantimos disponibilidade ininterrupta da plataforma em casos de força maior, falhas
                        em infraestrutura de terceiros, ataques cibernéticos ou outros eventos fora do nosso
                        controle razoável.
                    </p>
                </Section>

                <Section title="8. Limitação de Responsabilidade">
                    <p>
                        Na máxima extensão permitida pela legislação aplicável, a PawSuite não se responsabiliza por:
                    </p>
                    <ul className="list-disc list-inside space-y-2 mt-2 pl-2">
                        <li>Danos indiretos, incidentais, especiais ou consequentes decorrentes do uso ou impossibilidade de uso da plataforma;</li>
                        <li>Perda de receita, clientes ou dados causada por indisponibilidade do serviço fora dos limites do SLA;</li>
                        <li>Decisões comerciais tomadas com base nas informações e relatórios gerados pela plataforma;</li>
                        <li>Uso inadequado da plataforma pelo Usuário ou por terceiros com acesso às suas credenciais;</li>
                        <li>Conteúdo inserido pelo Usuário ou por seus colaboradores.</li>
                    </ul>
                    <p>
                        Em qualquer hipótese, a responsabilidade total da PawSuite ficará limitada ao valor pago
                        pelo Usuário nos <strong>três meses anteriores</strong> ao evento gerador do dano.
                    </p>
                </Section>

                <Section title="9. Cancelamento e Rescisão">
                    <p>
                        O Usuário pode cancelar sua assinatura a qualquer momento, diretamente no painel de configurações
                        da conta. O cancelamento entra em vigor ao final do período já pago, sem cobrança de multa.
                    </p>
                    <p>
                        Após o cancelamento, o Usuário terá <strong>30 dias</strong> para exportar seus dados.
                        Transcorrido esse prazo, os dados serão definitivamente excluídos dos nossos servidores,
                        conforme nossa Política de Privacidade.
                    </p>
                    <p>
                        A PawSuite poderá encerrar ou suspender o acesso imediatamente, sem aviso prévio, em caso
                        de violação grave destes Termos, conduta ilegal ou risco à segurança da plataforma ou de
                        outros usuários.
                    </p>
                </Section>

                <Section title="10. Proteção de Dados e Privacidade">
                    <p>
                        O tratamento de dados pessoais realizado pela PawSuite está descrito detalhadamente em nossa{" "}
                        <Link to="/privacidade" className="text-tealVitality hover:underline font-medium">
                            Política de Privacidade
                        </Link>
                        , que é parte integrante destes Termos de Uso.
                    </p>
                    <p>
                        O Usuário, ao cadastrar dados de clientes e tutores na plataforma, assume a condição de
                        controlador desses dados perante a LGPD, sendo responsável por obter as autorizações
                        necessárias dos respectivos titulares. A PawSuite atua como operadora desses dados, nos
                        termos do Acordo de Processamento de Dados disponível sob demanda.
                    </p>
                </Section>

                <Section title="11. Alterações nos Termos">
                    <p>
                        A PawSuite pode atualizar estes Termos a qualquer momento. Alterações serão comunicadas
                        por e-mail e/ou aviso na plataforma com antecedência mínima de <strong>15 dias</strong>
                        antes de entrarem em vigor.
                    </p>
                    <p>
                        O uso continuado da plataforma após a vigência das alterações será considerado aceitação
                        das novas condições. Caso o Usuário não concorde, deverá cancelar sua assinatura antes da
                        data de entrada em vigor.
                    </p>
                </Section>

                <Section title="12. Disposições Gerais">
                    <p>
                        Estes Termos constituem o acordo integral entre o Usuário e a PawSuite em relação ao uso
                        da plataforma, substituindo qualquer entendimento ou acordo anterior sobre o mesmo objeto.
                    </p>
                    <p>
                        Se qualquer disposição destes Termos for considerada inválida ou inexequível, as demais
                        disposições permanecerão em pleno vigor e efeito.
                    </p>
                    <p>
                        A tolerância da PawSuite quanto a qualquer descumprimento não constituirá renúncia ao
                        direito de exigir o cumprimento integral destes Termos no futuro.
                    </p>
                </Section>

                <Section title="13. Foro e Legislação Aplicável">
                    <p>
                        Estes Termos são regidos pelas leis da República Federativa do Brasil, em especial pelo
                        Código Civil (Lei nº 10.406/2002), pelo Código de Defesa do Consumidor (Lei nº 8.078/1990),
                        pelo Marco Civil da Internet (Lei nº 12.965/2014) e pela LGPD (Lei nº 13.709/2018).
                    </p>
                    <p>
                        Fica eleito o foro da Comarca de <strong>São Paulo – SP</strong> para dirimir quaisquer
                        controvérsias decorrentes destes Termos, com renúncia expressa a qualquer outro, por mais
                        privilegiado que seja.
                    </p>
                </Section>

                {/* CTA */}
                <div className="mt-12 p-8 bg-accentWarm rounded-2xl border border-orange-100 text-center">
                    <p className="text-midnight font-semibold mb-2">Dúvidas sobre os Termos?</p>
                    <p className="text-gray-500 text-sm mb-4">
                        Nossa equipe jurídica e de suporte está disponível para esclarecimentos.
                    </p>
                    <a
                        href="mailto:suporte@pawsuite.com.br"
                        className="inline-block bg-midnight text-white px-6 py-2.5 rounded-full font-bold text-sm hover:bg-gray-800 transition-all"
                    >
                        suporte@pawsuite.com.br
                    </a>
                </div>

                {/* Breadcrumb back */}
                <div className="mt-10 text-center">
                    <Link to="/" className="text-tealVitality text-sm font-medium hover:underline">
                        ← Voltar ao início
                    </Link>
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default TermsOfUse;
